import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../util/axiosConfig";
import "../../assets/css/container/main/MyInfo.css";
import { JOB_CATEGORY_LIST } from "../../global/globalProps";
import { useExpTable } from "../../global/ExpTableContext";
import LevelBadge from "../../global/LevelBadge";

const DUMMY_USER = {
	id: 0,
	email: "dummy@factweb.local",
	nickname: "더미유저",
	level: 1,
	exp: 120,
	credit: 50,
	region: "",
	job: "",
};

const DUMMY_EXP_TABLE = [
	{ level: 1, cumulativeExp: 0 },
	{ level: 2, cumulativeExp: 200 },
];

export default function MyInfo() {
	const navigate = useNavigate();
	const expTableFromContext = useExpTable();

	const [user, setUser] = useState<any>(null);
	const [nickname, setNickname] = useState("");
	const [region, setRegion] = useState("");
	const [job, setJob] = useState("");
	const [otherJob, setOtherJob] = useState("");
	const [loading, setLoading] = useState(true);

	/* =========================
	   사용자 정보 불러오기
	   ========================= */
	useEffect(() => {
		const fetchUser = async () => {
			try {
				const res = await axios.get("/api/user/me");
				setUser(res.data);
				setNickname(res.data.nickname || "");
				setRegion(res.data.region || "");

				const userJob = res.data.job || "";

				if (JOB_CATEGORY_LIST.includes(userJob)) {
					setJob(userJob);
					setOtherJob("");
				} else if (userJob) {
					setJob("기타");
					setOtherJob(userJob);
				} else {
					setJob("");
					setOtherJob("");
				}
			} catch {
				// ✅ 로컬 / 더미 환경
				setUser(DUMMY_USER);
				setNickname(DUMMY_USER.nickname);
				setRegion(DUMMY_USER.region);
				setJob(DUMMY_USER.job);
			} finally {
				setLoading(false);
			}
		};

		fetchUser();
	}, []);

	/* =========================
	   저장
	   ========================= */
	const handleUpdate = async (e: React.FormEvent) => {
		e.preventDefault();

		const saveJob = job === "기타" ? otherJob : job;

		try {
			await axios.put("/api/user/update", {
				nickname,
				region,
				job: saveJob,
			});
			alert("내 정보가 수정되었습니다.");
		} catch (err: any) {
			if (err.response?.status === 409) {
				alert(err.response.data || "이미 사용 중인 닉네임입니다.");
			} else {
				alert("수정 중 오류가 발생했습니다.");
			}
		}
	};

	/* =========================
	   회원 탈퇴
	   ========================= */
	const handleDeleteAccount = async () => {
		if (!window.confirm("정말 탈퇴하시겠습니까?")) return;

		try {
			await axios.put(`/api/user/${user.id}/deactivate`);
			localStorage.removeItem("token");
			navigate("/");
		} catch {
			alert("회원 탈퇴 중 오류가 발생했습니다.");
		}
	};

	if (loading) return <div>로딩중...</div>;

	/* =========================
	   실제 / 더미 테이블 결정
	   ========================= */
	const expTable =
		expTableFromContext.length > 0
			? expTableFromContext
			: DUMMY_EXP_TABLE;

	const current = expTable.find((e) => e.level === user.level);
	const next = expTable.find((e) => e.level === user.level + 1);

	const expIntoLevel = user.exp - (current?.cumulativeExp || 0);
	const expForNextLevel =
		(next?.cumulativeExp || user.exp) -
		(current?.cumulativeExp || 0);

	const expPercent = Math.min(
		100,
		((expIntoLevel / expForNextLevel) * 100) || 0
	).toFixed(1);

	return (
		<div className="myinfo-container">
			<h1 className="myinfo-title">나의 정보</h1>

			{/* ===== 요약 영역 ===== */}
			<div className="myinfo-summary">
				<p>
					<b>레벨:</b> <LevelBadge level={user.level} />
				</p>

				<div className="exp-bar">
					<div
						className="exp-bar-fill"
						style={{ width: `${expPercent}%` }}
					/>
					<span className="exp-percent">{expPercent}%</span>
				</div>

				<p>경험치: {expIntoLevel} / {expForNextLevel}</p>
				<p>누적 경험치: {user.exp}</p>
				<p>
					보유 크레딧: <b>{user.credit}</b>
				</p>
			</div>

			{/* ===== 수정 폼 ===== */}
			<form onSubmit={handleUpdate} className="myinfo-form">
				<div className="form-group">
					<label>이메일</label>
					<input type="text" value={user.email} readOnly />
				</div>

				<div className="form-group">
					<label>닉네임</label>
					<input
						type="text"
						value={nickname}
						onChange={(e) => setNickname(e.target.value)}
					/>
				</div>

				<div className="form-group">
					<label>지역</label>
					<select
						value={region}
						onChange={(e) => setRegion(e.target.value)}
					>
						<option value="">-- 선택 --</option>
						<option value="서울">서울</option>
						<option value="경기">경기</option>
						<option value="부산">부산</option>
						<option value="전남">전남</option>
						<option value="전북">전북</option>
						<option value="충남">충남</option>
						<option value="충북">충북</option>
						<option value="강원">강원</option>
						<option value="제주">제주</option>
					</select>
				</div>

				<div className="form-group">
					<label>직업</label>
					<select value={job} onChange={(e) => setJob(e.target.value)}>
						<option value="">직업 선택</option>
						{JOB_CATEGORY_LIST.map((item, idx) => (
							<option key={idx} value={item}>
								{item}
							</option>
						))}
					</select>

					{job === "기타" && (
						<input
							type="text"
							placeholder="직접 입력"
							value={otherJob}
							onChange={(e) => setOtherJob(e.target.value)}
						/>
					)}
				</div>

				<div className="form-group">
					<label>프로필 이미지</label>
					<div className="profile-placeholder">
						(추후 업로드 예정)
					</div>
				</div>

				<button type="submit" className="update-btn">
					수정하기
				</button>
			</form>

			<div className="delete-section">
				<button onClick={handleDeleteAccount} className="delete-btn">
					회원 탈퇴
				</button>
			</div>
		</div>
	);
}
